import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "label", "hint", "segment", "submit"]
  static values = {
    minimum: { type: String, default: "weak" }
  }

  connect() {
    this.update()
  }

  update() {
    const password = this.hasInputTarget ? this.inputTarget.value : ""
    const result = this.evaluate(password)
    const canSubmit = this.allowed(result)

    this.element.dataset.strength = result.key

    if (this.hasLabelTarget) {
      this.labelTarget.textContent = result.label
    }

    if (this.hasHintTarget) {
      this.hintTarget.textContent = this.hintFor(result, password, canSubmit)
    }

    this.segmentTargets.forEach((segment, index) => {
      segment.classList.toggle("is-active", index < result.segments)
    })

    if (this.hasInputTarget) {
      this.inputTarget.setCustomValidity(this.validationMessageFor(result, password, canSubmit))
    }

    this.submitTargets.forEach((submit) => {
      submit.disabled = !canSubmit
      submit.setAttribute("aria-disabled", canSubmit ? "false" : "true")
    })
  }

  validate(event) {
    this.update()

    if (!this.hasInputTarget || this.inputTarget.checkValidity()) return

    event.preventDefault()
    this.inputTarget.reportValidity()
    this.inputTarget.focus()
  }

  evaluate(password) {
    if (password.length === 0) {
      return {
        key: "empty",
        label: "Digite uma senha",
        hint: "Use letras, números e símbolos para aumentar a segurança.",
        segments: 0
      }
    }

    let score = 0

    if (password.length >= 6) score += 1
    if (password.length >= 10) score += 1
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1
    if (/\d/.test(password)) score += 1
    if (/[^A-Za-z0-9]/.test(password)) score += 1
    if (password.length >= 14) score += 1
    if (/(.)\1{2,}/.test(password)) score -= 1

    if (score >= 5) {
      return {
        key: "strong",
        label: "Forte",
        hint: "Boa senha. Ela combina tamanho e variedade de caracteres.",
        segments: 3
      }
    }

    if (score >= 3) {
      return {
        key: "medium",
        label: "Média",
        hint: "Está melhor. Adicione mais caracteres ou símbolos para fortalecer.",
        segments: 2
      }
    }

    return {
      key: "weak",
      label: "Fraca",
      hint: "Evite senhas curtas, repetidas ou muito previsíveis.",
      segments: 1
    }
  }

  allowed(result) {
    const ranks = {
      empty: 0,
      weak: 1,
      medium: 2,
      strong: 3
    }

    return ranks[result.key] >= ranks[this.minimumValue]
  }

  hintFor(result, password, canSubmit) {
    if (password.length > 0 && !canSubmit) {
      return `${result.hint} Use uma senha média ou forte para continuar.`
    }

    return result.hint
  }

  validationMessageFor(result, password, canSubmit) {
    if (password.length === 0 || canSubmit) return ""

    return "Use uma senha média ou forte para continuar."
  }
}
